import { DispositionCode } from "../../models/enums/DispositionCode";
import { MentionBuilder } from "../../models/builders/MentionBuilder";
import { getMentions } from "../reducers/interview/variablesUtilities";
import { Log } from "../../utils/logging";
import { MentionModel } from "../../models/MentionModel";
import { isNullOrEmpty } from "../../utils";
import { MiddlewareType, StoreType, ActionTypes } from "../actions/types";
import {
    AUDIO_RECORDING_START,
    AUDIO_RECORDING_STOP,
    CHANGE_LANGUAGE,
    CONNECTION_CONNECTED,
    CONNECTION_DISCONNECTED,
    CONNECTION_ERROR,
    CONNECTION_START,
    CONNECTION_PROGRESS,
    DEBUG_DISABLED,
    DEBUG_ENABLED,
    DEVICE_CHANGED,
    FILE_UPLOAD_STARTED,
    FILE_UPLOAD_COMPLETED,
    FILE_UPLOAD_FAILED,
    FILE_UPLOAD_CLEARED,
    LOG_ERROR,
    LOOKUP_TABLE_FETCH,
    LOOKUP_TABLE_RECEIVE,
    NAVIGATION_BRANCH_TO,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PAGE_READY,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_QUIT,
    NAVIGATION_RESET,
    NAVIGATION_SHORTCUT,
    OPEN_UPLOADED_FILE,
    QUOTAS_CHANGED,
    QUOTAS_CLOSE_STRATUM,
    QUOTAS_CLOSE_USER_STRATUM,
    SET_NAVIGATION_ENABLED,
    RECALL_TEXT,
    RESPONDENT_CHANGED,
    RESPONDENT_SET_APPOINTMENT,
    RESPONDENT_SET_EMAIL,
    RESPONDENT_SET_PHONE,
    SET_LOOP_ROW,
    SET_VARIABLE_MENTIONS,
    QUESTIONNAIRE_CHANGED,
    PREVIEW_THEME,
    VALIDATION_CLEAR,
    VALIDATION_SET_RESULT,
} from "../actions";

const TAG = "debugLogger";

function strvar(str: string, limit?: number, prefix?: string) {
    if (limit === undefined) limit = 10;
    if (prefix === undefined) prefix = "";
    if (isNullOrEmpty(str)) return "";
    str = str.replace(/<[^>]+>/g, "");
    str = str.replace(/\s/g, "˽");
    return prefix + (str.length > limit ? `${str.slice(0, limit - 1)}…` : str);
}

function mentionsToString(mentions: MentionModel[]) {
    const values = mentions.map((r) => strvar(r.value, 10, "") + strvar(r.openEnd, 10, "‒"));
    return `[${values.join(", ")}]`;
}

/**
 * Logs pretty-printed errors to the console.
 */
if (typeof window !== "undefined") {
    window.addEventListener("error", (ev) => {
        if (ev.error) Log.e(TAG, "ERROR", undefined, ev.error);
        else Log.e(TAG, "ERROR", "Error was undefined.");
    });
}

const logTypes = {
    [AUDIO_RECORDING_START]: (p) =>
        Log.i(TAG, "AUDIO_RECORDING_START", `for question ${p.questionName}`),
    [AUDIO_RECORDING_STOP]: (p) => Log.i(TAG, "AUDIO_RECORDING_STOP"),
    [CHANGE_LANGUAGE]: (p) => Log.i(TAG, "CHANGE_LANGUAGE", p),
    [CONNECTION_CONNECTED]: (p) => Log.i(TAG, "CONNECTION_CONNECTED", p.url),
    [CONNECTION_DISCONNECTED]: (p) => Log.w(TAG, "CONNECTION_DISCONNECTED", p.reason),
    [CONNECTION_ERROR]: (p) => Log.e(TAG, "CONNECTION_ERROR", p.error),
    [CONNECTION_START]: (p) => Log.i(TAG, "CONNECTION_START", p),
    [CONNECTION_PROGRESS]: (p) => Log.d(TAG, "CONNECTION_PROGRESS", `${Math.ceil(p * 100)}%`),
    [DEBUG_DISABLED]: (p) => Log.v(TAG, "DEBUG_DISABLED"),
    [DEBUG_ENABLED]: (p) => Log.v(TAG, "DEBUG_ENABLED"),
    [DEVICE_CHANGED]: (p) =>
        Log.d(TAG, "DEVICE_CHANGED", `${p.OS} ${p.OSVersion} ${p.model} (${p.type})`),
    [FILE_UPLOAD_STARTED]: (p) =>
        Log.d(TAG, "FILE_UPLOAD_STARTED", `${p.variableName}[${p.matrix}]`),
    [FILE_UPLOAD_COMPLETED]: (p) =>
        Log.d(TAG, "FILE_UPLOAD_COMPLETED", `${p.variableName}[${p.matrix}]`),
    [FILE_UPLOAD_FAILED]: (p) => Log.d(TAG, "FILE_UPLOAD_FAILED", `${p.variableName}[${p.matrix}]`),
    [FILE_UPLOAD_CLEARED]: (p) =>
        Log.d(TAG, "FILE_UPLOAD_CLEARED", `${p.variableName}[${p.matrix}]`),
    [LOG_ERROR]: (p) => {},
    [LOOKUP_TABLE_FETCH]: (p) => Log.d(TAG, "LOOKUP_TABLE_FETCH", `${p.tableId}`),
    [LOOKUP_TABLE_RECEIVE]: (p) =>
        Log.d(
            TAG,
            "LOOKUP_TABLE_RECEIVE",
            `${p.tableId}(${p.requestId}) → ${p.page.rows.length} rows`,
        ),
    [NAVIGATION_BRANCH_TO]: (p) =>
        Log.d(TAG, "NAVIGATION_BRANCH_TO", `${p.origin} → ${p.destination}`),
    [NAVIGATION_NEXT_PAGE]: (p) => Log.i(TAG, "NAVIGATION_NEXT_PAGE"),
    [NAVIGATION_PAGE_READY]: (p) => Log.d(TAG, "NAVIGATION_PAGE_READY"),
    [NAVIGATION_PREVIOUS_PAGE]: (p) => Log.i(TAG, "NAVIGATION_PREVIOUS_PAGE"),
    [NAVIGATION_QUIT]: (p) =>
        Log.i(TAG, "NAVIGATION_QUIT", `${DispositionCode[p.dispositionCode]} ${p.options || ""}`),
    [NAVIGATION_RESET]: (p) =>
        Log.d(TAG, "NAVIGATION_RESET", `clearAnswers: ${p ? "TRUE" : "FALSE"}`),
    [NAVIGATION_SHORTCUT]: (p) => Log.d(TAG, "NAVIGATION_SHORTCUT", p.blockName),
    [OPEN_UPLOADED_FILE]: (p) => Log.d(TAG, "OPEN_UPLOADED_FILE", p.variableName),
    [QUOTAS_CHANGED]: (p) => Log.d(TAG, "QUOTAS_CHANGED"),
    [QUOTAS_CLOSE_STRATUM]: (p) => Log.d(TAG, "QUOTAS_CLOSE_STRATUM", `stratumId: ${p}`),
    [QUOTAS_CLOSE_USER_STRATUM]: (p) => Log.d(TAG, "QUOTAS_CLOSE_USER_STRATUM", `stratumId: ${p}`),
    [SET_NAVIGATION_ENABLED]: (p) =>
        Log.d(TAG, "SET_NAVIGATION_ENABLED", p.enabled ? "TRUE" : "FALSE"),
    [RECALL_TEXT]: (p) => {}, // Log.v(TAG, "RECALL_TEXT", strvar(p.expression, 32)),
    [RESPONDENT_CHANGED]: (p) =>
        Log.d(
            TAG,
            "RESPONDENT_CHANGED",
            `id: ${p.respondentId} internalId: ${p.id || "(simulated)"}`,
        ),
    [RESPONDENT_SET_APPOINTMENT]: (p) => Log.d(TAG, "RESPONDENT_SET_APPOINTMENT", p),
    [RESPONDENT_SET_EMAIL]: (p) => Log.d(TAG, "RESPONDENT_SET_EMAIL", p),
    [RESPONDENT_SET_PHONE]: (p) => Log.d(TAG, "RESPONDENT_SET_PHONE", p),
    [SET_LOOP_ROW]: (p) => Log.d(TAG, "SET_LOOP_ROW", `${p.blockName}.Row = ${p.row}`),
    [SET_VARIABLE_MENTIONS]: (p, store) => {
        const variableState = store.getState().interview.variables;
        const mentions = getMentions(variableState, p.variableName, p.matrixIndex);
        const beforeMentions = mentionsToString(mentions);
        const afterMentions = mentionsToString(p.builder(new MentionBuilder(mentions)).build());
        Log.v(
            TAG,
            "SET_VARIABLE_MENTIONS",
            `${p.variableName}: ${beforeMentions} → ${afterMentions}`,
        );
    },
    [QUESTIONNAIRE_CHANGED]: (p) =>
        Log.v(TAG, "QUESTIONNAIRE_CHANGED", `${p.name} (id: ${p.id} v${p.version || 0})`),
    [PREVIEW_THEME]: (p) => Log.v(TAG, "PREVIEW_THEME"),
    [VALIDATION_CLEAR]: (p) => Log.v(TAG, "VALIDATION_CLEAR"),
    [VALIDATION_SET_RESULT]: (p) =>
        Log.v(TAG, "VALIDATION_SET_RESULT", `isValid: ${p.isValid ? "TRUE" : "FALSE"}`),
};

function logAction(store: StoreType, action: ActionTypes) {
    try {
        const p = action;
        if (logTypes[action.type] != null) return logTypes[action.type](p, store);
    } catch (ex) {
        Log.e(TAG, "EVENT_LOGGER_ERROR", "An error occurred logging an event.", ex);
    }
    return Log._(TAG, action.type);
}

/**
 * Logs state changes to the console.
 */
export const debugLogger: MiddlewareType = (store) => (next) => (action) => {
    logAction(store, action);
    return next(action);
};
